import React, { useState, useEffect, useCallback } from 'react'
import swal from 'sweetalert'
import FormikWizard  from 'formik-wizard'
import { useParams } from 'react-router-dom'
import { Box } from '@rockr/rock-ui'
import { useDispatch, useSelector } from 'react-redux'
import steps from './steps'
import { Footer } from '../../components/Footer'
import { Stepper } from '../../components/Stepper'
import { createContractService } from '../../shared/api/contracts'
import { OfferInput } from '../../shared/types/skus'
import { addOffers } from '../../shared/store/offers/actions'
import { Notification } from '../../components/Notification'
import { RootState } from '../../shared/store'
import { LegalType } from '../../shared/types/legal'
import { saveLegal } from '../../shared/store/legal/actions'
import { ContractEventItem, ContractEventPayload } from '../../shared/types/contract-event'
import { createGenericTypesService } from '../../shared/api/generic-types'
import { GenericTypes } from '../../shared/types/generic-types'
import { getBundleTotal } from '../../shared/helpers/bundle'

const FormWrapper = ({
  children,
  isLastStep,
  status,
  goToPreviousStep,
  canGoBack,
  currentStep,
  isSubmitting
}: any) => {

  useState(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    })
  })

  return (
    <div>
      <Stepper steps={steps} currentStep={currentStep} />

      {children}

      <Notification status="error" message={status?.message} show={status} />

      <Footer
        goToPreviousStep={goToPreviousStep}
        canGoBack={canGoBack}
        isLastStep={isLastStep}
        submitting={isSubmitting}
      />
    </div>
  )
}

export const Form: React.FC = () => {
  const { id: contractId } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const offers = useSelector<RootState, OfferInput[]>(state => state.offers)

  const [signerTypes, setSignerTypes] = useState<GenericTypes[]>([])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const parseInstallments = (installmentsRaw: any) => {
    const durationInYears = installmentsRaw.length / 12

    const installments = installmentsRaw
      .slice(0, installmentsRaw.length / durationInYears)

    return installments.map((installment: any) => ({
      value: installment.value,
      paymentDate: new Date(installment.date),
      paymentMethodId: installment.paymentMethod.id
    }))
  }

  const formatContractSkuFromAPI = useCallback((contractSku: any): OfferInput => {
    const [installment] = contractSku.installments

    return ({
      listPrice: contractSku.value,
      dealValue: contractSku.dealValue,
      quantity: contractSku.quantity,
      duration: contractSku.duration,
      originalIndex: contractSku.index,
      index: contractSku.index,
      isRenewable: true,
      isRecurring: contractSku.isRecurring,
      startDate: new Date(contractSku.startDate),
      displayName: contractSku.name,
      id: contractSku.contractEventOfferSkuId,
      skuCode: contractSku.type?.code ?? contractSku.skuCode,
      bundleOfferSkuId: contractSku.bundleOfferSkuId,
      offerSkuId: contractSku.offerSkuId,
      minValue: contractSku.minValue,
      maxValue: contractSku.maxValue,
      payment: {
        installments: parseInstallments(contractSku.installments),
        installmentFrequencyId: contractSku.installmentFrequency?.id || undefined,
        installmentPaymentMethodId: installment?.paymentMethod.id,
        firstInstallmentDate: installment ? new Date(installment?.date) : undefined,
        hasUpfront: contractSku.hasUpfront,
        upfrontDate: contractSku.hasUpfront ? new Date(contractSku.upfrontDate) : undefined,
        upfrontValue: contractSku.hasUpfront ? contractSku.upfrontValue : undefined,
        upfrontPaymentMethodId: contractSku.hasUpfront ? contractSku.upfrontPaymentMethodId : undefined
      }
    })
  } , [])

  const formatBundlesFromAPI = useCallback((bundle: any): OfferInput => {
    const discount = bundle.skus?.find((sku: any) => sku.value > 0)?.discount || 0
    const total = getBundleTotal(bundle, discount)
    const [firstSku] = bundle.skus
    const [firstInstallment] = firstSku.installments

    return {
      listPrice: total,
      dealValue: total - discount,
      quantity: 1,
      duration: 12,
      index: firstSku.index,
      isRenewable: firstSku.isRenewable,
      isRecurring: firstSku.isRecurring,
      startDate: firstSku.startDate,
      displayName: bundle.name,
      id: bundle.id,
      bundleId: bundle.id,
      offerSkuId: bundle.id,
      skuCode: firstSku.type.code,
      payment: {
        installments: firstSku.installments,
        installmentFrequencyId: firstSku.installmentFrequency.id,
        installmentPaymentMethodId: firstInstallment?.paymentMethod?.id,
        firstInstallmentDate: firstInstallment?.date,
        hasUpfront: firstSku.hasUpfront,
        upfrontDate: firstSku.hasUpfront ? new Date(firstSku.upfrontDate) : undefined,
        upfrontValue: firstSku.hasUpfront ? firstSku.upfrontValue : undefined,
        upfrontPaymentMethodId: firstSku.hasUpfront ? firstSku.upfrontPaymentMethodId : undefined
      },
      skus: bundle.skus.map((sku: any) => ({
        bundleOfferSkuId: sku.bundleOfferSkuId,
        id: sku.contractEventOfferSkuId,
        quantity: sku.quantity,
        recurring: sku.isRecurring,
        skuCode: sku.type.code,
        skuDisplayName: sku.name,
        skuName: sku.name,
        skuPlanName: sku.name,
        value: sku.value,
        offerSkuId: sku.offerSkuId,
        type: sku.type
      }))
    }
  }, [])

  const formatItemToAPIFormat = (offer: OfferInput): ContractEventItem => {
    return {
      payment: {
        hasUpfront: offer.payment.hasUpfront || false,
        upfrontDate: offer.payment.upfrontDate,
        upfrontValue: offer.payment.upfrontValue,
        installmentFrequencyId: offer.payment.installmentFrequencyId!,
        upfrontPaymentMethodId: offer.payment.upfrontPaymentMethodId,
        additionalInfo: offer.additionalInfo,
        firstInstallmentDate: offer.payment.firstInstallmentDate,
        paymentMethodId: offer.payment.installmentPaymentMethodId
      },
      billings: [], // TODO
      offerSkuId: offer.offerSkuId!,
      quantity: offer.quantity,
      duration: offer.duration,
      discount: offer.listPrice - offer.dealValue,
      discountTypeId: 1, // TODO
      commitment: offer.duration, // TODO FRONT
      isRenewable: offer.isRenewable,
      isRecurring: offer.isRecurring,
      index: offer.index,
      currencyId: 1, // TODO
      startDate: offer.startDate,
      bundleOfferSkuId: offer.bundleId ? offer.bundleOfferSkuId : undefined,
      isNew: !Boolean(offer.id),
      value: offer.listPrice,
      isChurn: offer.isChurn,
      parentId: offer.id,
      isUpdated: Boolean(offer.isUpdated)
    }
  }

  const formatLegalToAPIFormat = (legal: any) => {
    const signerType = signerTypes.find(type => type.description === 'Signer')

    if (legal.signers?.length === 0){
      throw new Error('The signers is required')
    } else if (!legal.signers.find((signer: any) => signer.typeId === signerType?.id)){
      throw new Error('You must fill at least one email of "signer" type')
    }

    if(legal.isNew && !legal.salesQueueId) {
      throw new Error('Sales queue is required')
    }

    return {
      isAssistedByPartner: legal.isAssistedByPartner,
      hasNda: legal.hasNda,
      additionalInfo: legal.additionalInfo,
      billingCountryId: legal.billingCountryId,
      geographicSegmentationId: legal.geographicSegmentationId,
      salesQueueId: legal.salesQueueId,
      signers: legal.signers,
      startDate: legal.startDate
    }
  }

  useEffect(() => {
    const contractService = createContractService()
    const genericTypesService = createGenericTypesService()

    if (contractId) {
      contractService.getSkusFromContract(Number(contractId))
        .then(({ skus, bundles }) => {
          return [
            ...skus.map(formatContractSkuFromAPI),
            ...bundles.map(formatBundlesFromAPI)
          ]
        })
        .then((offers: OfferInput[]) => dispatch(addOffers(offers)))

      contractService.getLegal(Number(contractId))
        .then((legal: LegalType) => dispatch(saveLegal({
          ...legal,
          salesQueueId: undefined,
          billingCountryId: legal.locationId || undefined
        })))
    }

    if (signerTypes?.length === 0) {
      genericTypesService.getSignerTypes()
        .then((types) => setSignerTypes(types))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, dispatch, formatContractSkuFromAPI])

  const getSkusFromBundles = useCallback(
    (offers: OfferInput[], offer: OfferInput) => {
      if (!offer.bundleId) {
        return [...offers, offer]
      }

      if (!offer.skus || offer.skus.length === 0) {
        return offers
      }
      const formattedSkus = offer.skus.map(sku => {
        return {
          ...offer,
          offerSkuId: sku.offerSkuId || sku.id,
          listPrice: sku.value,
          dealValue: sku.value > 0 ? offer.dealValue : 0,
          displayName: sku.skuName,
          bundleOfferSkuId: sku.bundleOfferSkuId
        }
      })
      return [...offers, ...formattedSkus]
    }, [])

  const handleSubmit = useCallback((values) => {
    const contractService = createContractService()
    const { legal } = values

    const items = offers.reduce<OfferInput[]>(getSkusFromBundles, [])

    const payload: ContractEventPayload = {
      contractId: Number(contractId),
      isContractAutorenewable: true || legal.isContractAutorenewable, // TODO FRONT
      legal: formatLegalToAPIFormat(legal),
      items: items.map(formatItemToAPIFormat)
    }
    
    setIsSubmitting(true)

    contractService.createContractEvent(payload)
      .then(() => setIsSubmitting(false))
      .then(async () => {
        await swal({
          title: "Good job!",
          text: "Event created!",
          icon: "success"
        }).then(() => {
          window.parent.postMessage("FORM_FINISHED", "*")
        })
      })
      .catch(() => {
        swal({
          title: "An error just occurred",
          text: "We couldn't process your event submit, try again later!",
          icon: "error"
        })

        setIsSubmitting(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers, contractId, getSkusFromBundles])

  const renderFormWrapper = useCallback(props => {
    return FormWrapper({
      ...props,
      isSubmitting
    })
  }, [isSubmitting])

  return (
    <Box padding={25}>
      <FormikWizard
        steps={steps}
        onSubmit={handleSubmit}
        render={renderFormWrapper}
      />
    </Box>
  )
}
